import { type BoardChange, type BoardView } from '@shared/session'
import { clsx } from 'clsx'
import { ChevronRight } from 'lucide-react'
import { Fragment, type ReactNode, useState } from 'react'
import { useI18n } from '@/i18n/i18n'
import { useActorName, useSpeaker } from './actorName'
import { MentionChip } from './SayText'
import { actorKey, groupChanges, VERB } from './changes'
import { boardSummary } from './turns'

// What the AI was told about the board at the start of a turn, for people:
// who changed what since its previous turn, by name. The exact text it read
// stays in the trace (DESIGN.md: the diff is trace; one line, full on demand).

function Things({ changes, max }: { changes: BoardChange[]; max: number }) {
  const { t } = useI18n()
  return (
    <>
      {changes.slice(0, max).map((c) =>
        c.op === 'removed' ? (
          <span key={c.id} className="line-through decoration-muted/60">
            「{c.label || t(c.kind === 'edge' ? 'board.anEdge' : 'board.anItem')}」
          </span>
        ) : (
          <MentionChip
            key={c.id}
            mention={c.kind === 'edge' ? { kind: 'edge', id: c.id } : { kind: 'item', id: c.id }}
            fallback="…"
          />
        ),
      )}
      {changes.length > max && t('act.more', { n: changes.length - max })}
    </>
  )
}

/** The reasons another session gave for these changes, if any. */
function Why({ changes }: { changes: BoardChange[] }) {
  const reasons = [...new Set(changes.flatMap((c) => (c.intent ? [c.intent] : [])))]
  if (!reasons.length) return null
  return <span className="ml-1 text-ink-2">（{reasons.join('；')}）</span>
}

function Row({
  children,
  onToggle,
  open,
}: {
  children: ReactNode
  onToggle?: () => void
  open?: boolean
}) {
  const { t } = useI18n()
  return (
    <div
      className={clsx('flex items-start gap-1', onToggle && 'cursor-pointer')}
      onClick={(e) => {
        if (onToggle && !(e.target as HTMLElement).closest('[data-mention],[data-raw]')) onToggle()
      }}
    >
      {onToggle ? (
        <button
          type="button"
          aria-expanded={open}
          aria-label={t('board.toggle')}
          className="mt-1.5 flex-none hover:text-ink"
        >
          <ChevronRight size={12} className={clsx('transition-transform', open && 'rotate-90')} />
        </button>
      ) : (
        <span className="w-3 flex-none" />
      )}
      <span className="min-w-0">{children}</span>
    </div>
  )
}

export function BoardLine({
  text,
  view,
  sessionId,
  onRaw,
  pending = false,
}: {
  text: string
  view: BoardView | null
  sessionId: string
  /** Show the exact text the AI read (the trace). Without it, the text opens in place. */
  onRaw?: () => void
  /**
   * Not sent yet: what the next turn would be told (by the composer). Looks
   * unlike the lines of past turns, and says so.
   */
  pending?: boolean
}) {
  const { t } = useI18n()
  const name = useActorName()
  const who = useSpeaker(sessionId)
  const [open, setOpen] = useState(false)
  const [showRaw, setShowRaw] = useState(false)
  const raw = (
    <button
      type="button"
      data-raw=""
      aria-expanded={onRaw ? undefined : showRaw}
      onClick={
        onRaw ??
        (() => {
          setShowRaw((v) => !v)
        })
      }
      className="ml-1.5 underline decoration-dotted underline-offset-2 hover:text-ink"
    >
      {t('board.raw')}
    </button>
  )
  const rawText = showRaw && (
    <pre className="mt-1.5 max-h-56 overflow-auto rounded-md border border-line bg-surface-2 px-2.5 py-2 font-mono text-[11px] leading-relaxed whitespace-pre-wrap text-ink-2 [overflow-wrap:anywhere]">
      {text}
    </pre>
  )
  const frame = clsx(
    'text-[12px] leading-6 text-muted',
    pending && 'rounded-md border border-dashed border-line-strong px-2 py-1',
  )
  const tag = pending && (
    <span className="mr-1.5 rounded-sm bg-surface-2 px-1 py-px text-[10.5px] font-medium tracking-wide text-ink-2 uppercase">
      {t('pending.tag')}
    </span>
  )

  // Older turns have no structured view: count from the text.
  const sum = view?.kind === 'snapshot' ? view : !view ? boardSummary(text) : null
  if (sum) {
    const label =
      sum.kind === 'changes'
        ? t('chat.boardChanges', { n: sum.count, who })
        : pending
          ? t('pending.snapshot', { n: sum.count })
          : sum.count
            ? t('chat.boardSnapshot', { n: sum.count, who })
            : t('chat.boardEmpty', { who })
    return (
      <div className={frame}>
        <Row>
          {tag}
          {label}
          {raw}
        </Row>
        {rawText}
      </div>
    )
  }

  const groups = groupChanges(view?.kind === 'changes' ? view.changes : [], sessionId)
  if (!groups.length) return null
  const total = groups.reduce((n, g) => n + g.ops.reduce((m, o) => m + o.changes.length, 0), 0)
  const first = groups[0]
  const firstOp = first?.ops[0]
  if (!first || !firstOp) return null
  const rest = total - Math.min(firstOp.changes.length, 2)
  return (
    <div className={frame}>
      <Row
        open={open}
        onToggle={() => {
          setOpen((v) => !v)
        }}
      >
        {tag}
        {t(pending ? 'pending.saw' : 'board.saw')}
        {name(first.actor)}
        {t(VERB[firstOp.op])}
        <Things changes={firstOp.changes} max={2} />
        {rest > 0 && t('board.more', { n: rest })}
      </Row>
      {open && (
        <ul className="mt-1 ml-1.5 space-y-1 border-l border-line pl-3">
          {groups.map((g) => (
            <li key={actorKey(g.actor)}>
              <span className="text-ink-2">{name(g.actor)}</span>
              {g.ops.map((o, i) => (
                <Fragment key={o.op}>
                  {i > 0 && t('act.sep')}
                  {t(VERB[o.op])}
                  <Things changes={o.changes} max={12} />
                  <Why changes={o.changes} />
                </Fragment>
              ))}
            </li>
          ))}
          <li>{raw}</li>
        </ul>
      )}
      {rawText}
    </div>
  )
}
